import { createApi } from '@reduxjs/toolkit/query/react';

import { HttpMethod, createBaseQuery } from '../base';
import type {
  HistoricalScenario,
  HistoricalScenariosListResponse,
  // HistoricalScenariosCalculationResponse,
} from './types';
import { ApiResponse } from '../types';

import { removeSelectedHistoricalScenario, updateSelectedHistoricalScenario } from '@/redux/modules/comparison';
import CalculatedHistoricalScenario from '@/types/calculated-historical-scenario';
import RebalanceFrequency from '@/types/rebalance-frequency';

// API Endpoints
const ENDPOINTS = {
  CALCULATE_COMPARISON_HISTORICAL_SCENARIOS: '/comparisons/:id/historical-scenarios',
  HISTORICAL_SCENARIOS: '/historical-scenarios',
  HISTORICAL_SCENARIOS_CALCULATE: '/historical-scenarios/calculate',
} as const;

// Tag Type Constants
const HISTORICAL_SCENARIOS_TAG = 'HistoricalScenarios' as const;
const CALCULATION_TAG = 'Calculation' as const;

// Cache ID Constants
const LIST_ID = 'LIST' as const;

// Tag Types
const TAG_TYPES = [HISTORICAL_SCENARIOS_TAG, CALCULATION_TAG] as const;

// Cache Tags
const CACHE_TAGS = {
  SCENARIOS_LIST: { type: HISTORICAL_SCENARIOS_TAG, id: LIST_ID },
  SCENARIO: (scenarioId: string) => ({ type: HISTORICAL_SCENARIOS_TAG, id: scenarioId }),
  CALCULATIONS: { type: CALCULATION_TAG, id: LIST_ID },
} as const;

export const historicalScenariosApi = createApi({
  reducerPath: 'historicalScenariosApi',
  baseQuery: createBaseQuery(),
  tagTypes: TAG_TYPES,
  endpoints: builder => ({
    // Get all available scenarios (predefined + custom)
    getAvailableScenarios: builder.query<HistoricalScenariosListResponse, void>({
      query: () => ENDPOINTS.HISTORICAL_SCENARIOS,
      providesTags: [CACHE_TAGS.SCENARIOS_LIST],
      keepUnusedDataFor: 1800, // 30 minutes - scenarios don't change often
      transformResponse: (response: ApiResponse<{ predefined: HistoricalScenario[]; custom: HistoricalScenario[] }>): HistoricalScenariosListResponse => {
        const { predefined = [], custom = [] } = response?.data || {};
        return {
          predefinedScenarios: predefined,
          customScenarios: custom,
          availableScenarios: [...predefined, ...custom],
        };
      },
    }),

    calculateHistoricalScenarios: builder.query<
      CalculatedHistoricalScenario[],
      {
        historicalScenarios: HistoricalScenario[];
        items: any[];
        togetherMode: boolean;
        baseCurrencyEnabled?: boolean;
        rebalanceFrequency?: RebalanceFrequency;
        shortHistory?: boolean;
      }
    >({
      query: ({ historicalScenarios, items, togetherMode, baseCurrencyEnabled, rebalanceFrequency, shortHistory }) => ({
        url: ENDPOINTS.HISTORICAL_SCENARIOS_CALCULATE,
        method: HttpMethod.POST,
        body: { historicalScenarios, items, togetherMode, baseCurrencyEnabled, rebalanceFrequency, shortHistory },
      }),
      providesTags: [CACHE_TAGS.CALCULATIONS],
    }),

    calculateComparisonHistoricalScenarios: builder.query<
      CalculatedHistoricalScenario[],
      { comparisonId: string; contactId?: string; historicalScenarios: HistoricalScenario[]; togetherMode: boolean; rebalanceFrequency?: RebalanceFrequency }
    >({
      query: ({ comparisonId, contactId, historicalScenarios, togetherMode, rebalanceFrequency }) => ({
        url: ENDPOINTS.CALCULATE_COMPARISON_HISTORICAL_SCENARIOS.replace(':id', comparisonId),
        params: contactId ? { contactId } : undefined,
        method: HttpMethod.POST,
        body: { historicalScenarios, togetherMode, rebalanceFrequency },
      }),
      providesTags: [CACHE_TAGS.CALCULATIONS],
    }),

    createHistoricalScenario: builder.mutation<HistoricalScenario, Partial<HistoricalScenario>>({
      query: scenario => ({
        url: ENDPOINTS.HISTORICAL_SCENARIOS,
        method: HttpMethod.POST,
        body: scenario,
      }),
      invalidatesTags: [CACHE_TAGS.SCENARIOS_LIST],
      transformResponse: (response: ApiResponse<HistoricalScenario>) => response.data,
    }),

    updateHistoricalScenario: builder.mutation<HistoricalScenario, { scenarioId: string; scenario: Partial<HistoricalScenario> }>({
      query: ({ scenarioId, scenario }) => ({
        url: `${ENDPOINTS.HISTORICAL_SCENARIOS}/${scenarioId}/`,
        method: HttpMethod.PATCH,
        body: scenario,
      }),
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const { data: updatedScenario } = await queryFulfilled;
        dispatch(updateSelectedHistoricalScenario(updatedScenario));
      },
      invalidatesTags: (_result, _error, { scenarioId }) => [CACHE_TAGS.SCENARIOS_LIST, CACHE_TAGS.SCENARIO(scenarioId), CACHE_TAGS.CALCULATIONS],
      transformResponse: (response: ApiResponse<HistoricalScenario>) => response.data,
    }),

    deleteHistoricalScenario: builder.mutation<HistoricalScenario, string>({
      query: scenarioId => ({
        url: `${ENDPOINTS.HISTORICAL_SCENARIOS}/${scenarioId}/`,
        method: HttpMethod.DELETE,
      }),
      onQueryStarted: async (scenarioId, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(removeSelectedHistoricalScenario(scenarioId));
      },
      invalidatesTags: (_result, _error, scenarioId) => [CACHE_TAGS.SCENARIOS_LIST, CACHE_TAGS.SCENARIO(scenarioId), CACHE_TAGS.CALCULATIONS],
      transformResponse: (response: ApiResponse<HistoricalScenario>) => response.data,
    }),
  }),
});

export const {
  useCalculateHistoricalScenariosQuery,
  useCalculateComparisonHistoricalScenariosQuery,
  useGetAvailableScenariosQuery,
  // Mutation hooks
  useCreateHistoricalScenarioMutation,
  useUpdateHistoricalScenarioMutation,
  useDeleteHistoricalScenarioMutation,
} = historicalScenariosApi;

export default historicalScenariosApi;
